import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatDate } from "@/lib/format";
import { Star, Pin, PinOff, Check, X, Trash2, Filter } from "lucide-react";

const TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function FeedbackAdmin() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [enabled, setEnabled] = useState(true);

  const load = async () => {
    try {
      const p = { params: {} };
      if (tab) p.params.status = tab;
      const { data } = await api.get("/admin/feedback", p);
      setItems(data);
    } catch (e) { console.error(e); }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setEnabled(data.feedback_enabled !== false);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { loadSettings(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const togglePin = async (id, cur) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { pinned: !cur });
      toast.success(cur ? "Unpinned" : "Pinned to top");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleSection = async () => {
    try {
      const nv = !enabled;
      await api.patch("/admin/feedback", { enabled: nv });
      setEnabled(nv);
      toast.success(nv ? "Feedback section enabled on landing" : "Feedback section hidden on landing");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Moderation"
        title="Feedback & Reviews"
        right={
          <button
            onClick={toggleSection}
            className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] border transition-colors ${enabled ? "border-[#fbce18]/40 text-[#fbce18]" : "border-white/10 text-white/60"}`}
            data-testid="feedback-section-toggle"
          >
            Landing section: {enabled ? "ON" : "OFF"}
          </button>
        }
      />

      <div className="ldc-glass p-2 mb-4 inline-flex gap-1" data-testid="feedback-tabs">
        {TABS.map((t) => (
          <button
            key={t.key || "all"}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2 ${tab === t.key ? "bg-[#fbce18] text-black" : "text-white/60 hover:text-white"}`}
            data-testid={`feedback-tab-${t.key || "all"}`}
          >
            <Filter size={12}/> {t.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3" data-testid="feedback-admin-list">
        {items.length === 0 && (<div className="ldc-glass p-10 text-center text-white/50">Nothing here in this queue.</div>)}
        {items.map((f) => (
          <div key={f.id} className="ldc-glass p-5" data-testid={`feedback-admin-${f.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-[260px]">
                {f.avatar ? (
                  <img src={f.avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-[#fbce18]/15 text-[#fbce18]">{f.user_name?.[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    <div className="font-medium">{f.user_name}</div>
                    <span className="text-xs text-white/40">{f.user_email}</span>
                    {typeof f.rating === "number" && (
                      <div className="flex gap-0.5 ml-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={12} fill={i < f.rating ? "#fbce18" : "transparent"} color={i < f.rating ? "#fbce18" : "rgba(255,255,255,0.25)"} />
                        ))}
                      </div>
                    )}
                    {f.pinned && <span className="text-[10px] text-[#fbce18] inline-flex items-center gap-0.5"><Pin size={10}/> Pinned</span>}
                  </div>
                  <div className="text-sm text-white/85 mt-2 leading-relaxed">{f.comment}</div>
                  <div className="mt-2 text-[11px] text-white/40">
                    {f.service ? `${f.service} · ` : ""}{formatDate(f.created_at)}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 items-start">
                <StatusPill status={f.status} />
                {f.status !== "approved" && (
                  <button onClick={() => setStatus(f.id, "approved")} className="p-2 rounded hover:bg-emerald-500/10 text-emerald-400 transition-colors" title="Approve" data-testid={`feedback-approve-${f.id}`}>
                    <Check size={16}/>
                  </button>
                )}
                {f.status !== "rejected" && (
                  <button onClick={() => setStatus(f.id, "rejected")} className="p-2 rounded hover:bg-red-500/10 text-white/60 hover:text-red-400 transition-colors" title="Reject" data-testid={`feedback-reject-${f.id}`}>
                    <X size={16}/>
                  </button>
                )}
                <button onClick={() => togglePin(f.id, f.pinned)} className={`p-2 rounded hover:bg-[#fbce18]/10 transition-colors ${f.pinned ? "text-[#fbce18]" : "text-white/60"}`} title={f.pinned ? "Unpin" : "Pin to top"} data-testid={`feedback-pin-${f.id}`}>
                  {f.pinned ? <PinOff size={16}/> : <Pin size={16}/>}
                </button>
                <button onClick={() => del(f.id)} className="p-2 rounded hover:bg-red-500/10 text-white/60 hover:text-red-400 transition-colors" title="Delete" data-testid={`feedback-delete-${f.id}`}>
                  <Trash2 size={16}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "approved") return <span className="ldc-badge ldc-badge-paid">Approved</span>;
  if (status === "rejected") return <span className="ldc-badge ldc-badge-failed">Rejected</span>;
  return <span className="ldc-badge ldc-badge-pending">Pending</span>;
}
